/**
 * Immutable helpers on dotted relative paths (`0.destinations.2.city`) —
 * used by the edit runtime to fold a row/cell edit into the top-level field
 * value it commits as a whole.
 */

const segmentsOf = (path) =>
  path == null || path === '' ? [] : String(path).split('.')

const isIndex = (segment) => /^\d+$/.test(segment)

export const getAtPath = (root, path) =>
  segmentsOf(path).reduce((acc, segment) => (acc == null ? undefined : acc[segment]), root)

/** Returns a new tree with `value` set at `path`; intermediate containers are cloned (arrays for numeric segments). */
export const setAtPath = (root, path, value) => {
  const segments = segmentsOf(path)
  if (segments.length === 0) return value
  const [head, ...rest] = segments
  const container = root == null
    ? (isIndex(head) ? [] : {})
    : (Array.isArray(root) ? [...root] : { ...root })
  const key = isIndex(head) && Array.isArray(container) ? Number(head) : head
  container[key] = setAtPath(root?.[head], rest.join('.'), value)
  return container
}

/**
 * Apply a row action to an array and return the new array (never mutates).
 * `index` is the row the action refers to (`null` for a plain append);
 * `toIndex` is the destination of a `move` (drag & drop, or a keyboard
 * reorder), expressed in the indices of the list as it stands — the row is
 * pulled out before being re-inserted.
 */
export const applyRowAction = (list, action, index, defaultItem, toIndex = null) => {
  const items = Array.isArray(list) ? [...list] : []
  const fresh = () => (defaultItem == null ? {} : structuredClone(defaultItem))
  const swap = (a, b) => { [items[a], items[b]] = [items[b], items[a]] }
  switch (action) {
    case 'add':
      if (index == null) items.push(fresh())
      else items.splice(index + 1, 0, fresh())
      return items
    case 'insert':
      items.splice((index ?? items.length - 1) + 1, 0, fresh())
      return items
    case 'remove':
      if (index != null && index >= 0 && index < items.length) items.splice(index, 1)
      return items
    case 'up':
      if (index != null && index > 0) swap(index, index - 1)
      return items
    case 'down':
      if (index != null && index < items.length - 1) swap(index, index + 1)
      return items
    case 'move': {
      if (index == null || toIndex == null) return items
      if (index < 0 || index >= items.length || index === toIndex) return items
      const [row] = items.splice(index, 1)
      items.splice(Math.max(0, Math.min(toIndex, items.length)), 0, row)
      return items
    }
    default:
      return items
  }
}
